import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useNotifications } from './notifications';
import axios from 'axios';
import { sanitizeRedirectUrl, validatePasswordStrength, loginRateLimiter } from '../utils/security';
import { BRAND_NAME, getBrandLogoSrc } from '../utils/brandAssets';
import { API_BASE_URL } from '../api/axiosConfig';
import './Login.css';

const Login = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showCreateAccount, setShowCreateAccount] = useState(false);
    const [showForgotPassword, setShowForgotPassword] = useState(false);
    const [justLoggedIn, setJustLoggedIn] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const { login, authenticated, loading, googleLogin } = useAuth();
    const { showSuccess, showError, NotificationComponent } = useNotifications();

    useEffect(() => {
        // Only redirect if already authenticated on mount, but not if we just logged in
        if (!loading && authenticated && !justLoggedIn) {
            const urlParams = new URLSearchParams(location.search);
            const redirectUrl = urlParams.get('redirect');
            // 🔒 SECURITY: Sanitize redirect URL to prevent open redirect attacks
            const safeRedirectUrl = sanitizeRedirectUrl(redirectUrl);
            if (safeRedirectUrl !== '/dashboard') {
                window.location.href = safeRedirectUrl;
            } else {
                navigate('/dashboard');
            }
        }
    }, [authenticated, navigate, location.search, loading, justLoggedIn]);

    useEffect(() => {
        // Clear form on mount
        setEmail('');
        setPassword('');

        // Check for Google auth errors in URL
        const urlParams = new URLSearchParams(window.location.search);
        const authStatus = urlParams.get('auth_status');
        const message = urlParams.get('message');

        if (authStatus === 'error' && message) {
            showError(decodeURIComponent(message));
            window.history.replaceState({}, document.title, window.location.pathname);
        }
    }, [location.search, showError]);

    if (loading || (!justLoggedIn && authenticated)) {
        return (
            <div className="flex items-center justify-center h-screen">
                <div className="text-lg font-medium text-gray-600">
                    Loading...
                </div>
            </div>
        );
    }

    const handleLogin = async (e) => {
        e.preventDefault();

        if (!email) {
            showError('Please enter your email address');
            return;
        }

        if (!password) {
            showError('Please enter your password');
            return;
        }

        // 🔒 SECURITY: Rate limiting to prevent brute force attacks
        const rateLimitCheck = loginRateLimiter.checkLimit(email);
        if (!rateLimitCheck.allowed) {
            const resetTime = rateLimitCheck.resetTime.toLocaleTimeString();
            showError(`Too many login attempts. Please try again after ${resetTime}`);
            return;
        }

        setIsSubmitting(true);

        try {
            const result = await login(email, password);

            if (result.success) {
                // Reset rate limiter on successful login
                loginRateLimiter.reset(email);
                
                // Prevent useEffect redirect from interfering
                setJustLoggedIn(true);

                showSuccess('Login successful! Redirecting...');

                // 🔒 SECURITY: Sanitize redirect URL to prevent open redirect attacks
                const urlParams = new URLSearchParams(location.search);
                const redirectUrl = urlParams.get('redirect');
                const safeRedirectUrl = sanitizeRedirectUrl(redirectUrl);

                setTimeout(() => {
                    if (safeRedirectUrl !== '/dashboard') {
                        window.location.href = safeRedirectUrl;
                    } else {
                        navigate('/dashboard');
                    }
                }, 500);
            } else {
                showError(result.message || 'Login failed');
                if (rateLimitCheck.remainingAttempts > 0) {
                    showError(`${rateLimitCheck.remainingAttempts} attempts remaining`);
                }
            }
        } catch (error) {
            showError('An unexpected error occurred');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleGoogleLogin = () => {
        const urlParams = new URLSearchParams(location.search);
        const redirectUrl = urlParams.get('redirect');
        googleLogin(redirectUrl);
    };

    const toggleCreateAccount = () => {
        setShowCreateAccount(!showCreateAccount);
        setShowForgotPassword(false);
    };

    return (
        <div className="login-container">
            <NotificationComponent />
            <div className={`login-card ${showCreateAccount ? 'create-account-mode' : ''}`}>
                <div className="login-header">
                    <div className="logo-container">
                        <img
                            src={getBrandLogoSrc()}
                            alt={`${BRAND_NAME} Logo`}
                            referrerPolicy="no-referrer"
                        />
                    </div>
                    <h1 className="login-title">{BRAND_NAME}</h1>
                    <p className="login-subtitle">
                        {showCreateAccount ? 'Create your account' : showForgotPassword ? 'Reset your password' : 'Sign in to your account'}
                    </p>
                </div>

                {!showCreateAccount && !showForgotPassword ? (
                    <form onSubmit={handleLogin} className="login-form">
                        <div className="form-group">
                            <label htmlFor="email">Email address</label>
                            <input
                                id="email"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="Enter your email"
                                className="form-input"
                                autoComplete="email"
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="password">Password</label>
                            <div className="password-input-container">
                                <input
                                    id="password"
                                    type={showPassword ? 'text' : 'password'}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="Enter your password"
                                    className="form-input"
                                    autoComplete="current-password"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="password-toggle"
                                >
                                    {showPassword ? '👁️' : '👁️‍🗨️'}
                                </button>
                            </div>
                            <div className="forgot-password-container">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowForgotPassword(true);
                                    }}
                                    className="link-button forgot-password-link"
                                >
                                    Forgot password?
                                </button>
                            </div>
                        </div>

                        <div className="form-actions">
                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className="btn-primary"
                            >
                                {isSubmitting ? 'Signing in...' : 'Sign in'}
                            </button>
                        </div>

                        <div className="divider">
                            <span>OR</span>
                        </div>

                        <button
                            type="button"
                            onClick={handleGoogleLogin}
                            className="btn-google"
                        >
                            <svg width="18" height="18" viewBox="0 0 18 18">
                                <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" />
                                <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332C2.438 15.983 5.482 18 9 18z" />
                                <path fill="#FBBC05" d="M3.964 10.71c-.18-.54-.282-1.117-.282-1.71s.102-1.17.282-1.71V4.958H.957C.347 6.173 0 7.548 0 9s.348 2.827.957 4.042l3.007-2.332z" />
                                <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0 5.482 0 2.438 2.017.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" />
                            </svg>
                            Continue with Google
                        </button>

                        <div className="login-footer">
                            <p>
                                Don't have an account?{' '}
                                <button
                                    type="button"
                                    onClick={toggleCreateAccount}
                                    className="link-button"
                                >
                                    Create account
                                </button>
                            </p>
                        </div>
                    </form>
                ) : showCreateAccount ? (
                    <CreateAccountForm
                        onCancel={toggleCreateAccount}
                        onSuccess={() => {
                            setShowCreateAccount(false);
                        }}
                        showSuccess={showSuccess}
                        showError={showError}
                    />
                ) : showForgotPassword ? (
                    <ForgotPasswordForm
                        onCancel={() => {
                            setShowForgotPassword(false);
                        }}
                        onSuccess={() => {
                            setShowForgotPassword(false);
                        }}
                        showSuccess={showSuccess}
                        showError={showError}
                    />
                ) : null}
            </div>
        </div>
    );
};

const CreateAccountForm = ({ onCancel, onSuccess, showSuccess, showError }) => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        password: '',
        confirmPassword: ''
    });
    const [isLoading, setIsLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;

        // Special handling for phone number - only allow numbers
        if (name === 'phone') {
            // Remove all non-digit characters
            const digitsOnly = value.replace(/\D/g, '');

            setFormData({
                ...formData,
                [name]: digitsOnly
            });
        } else {
            setFormData({
                ...formData,
                [name]: value
            });
        }
    };

    const handleGoogleSignup = () => {
        window.location.href = `${API_BASE_URL}/api/auth/google`;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validation
        if (!formData.name || !formData.email || !formData.phone || !formData.password) {
            showError('Please fill in all required fields');
            return;
        }

        // Validate phone number (digits only, will be prefixed with + when sending)
        const phoneRegex = /^[1-9]\d{1,14}$/;
        if (!phoneRegex.test(formData.phone)) {
            showError('Please enter a valid phone number with country code (e.g., 1234567890)');
            return;
        }

        if (formData.password !== formData.confirmPassword) {
            showError('Passwords do not match');
            return;
        }

        // 🔒 SECURITY: Enforce strong password policy
        const passwordValidation = validatePasswordStrength(formData.password);
        if (!passwordValidation.isValid) {
            showError(passwordValidation.message);
            return;
        }

        setIsLoading(true);

        try {
            // Check if email is already in use
            const checkEmailResponse = await fetch(`${API_BASE_URL}/api/user/validate/email`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ email: formData.email }),
            });

            const checkEmailResult = await checkEmailResponse.json();

            if (checkEmailResult.exists) {
                showError(checkEmailResult.message || 'This email is already registered. Please log in or use a different email.');
                setIsLoading(false);
                return;
            }

            // Create the account
            const response = await axios.post(`${API_BASE_URL}/api/user/create`, {
                name: formData.name,
                email: formData.email,
                phone: '+' + formData.phone,
                password: formData.password
            });

            if (response.data.success) {
                showSuccess('Account created successfully! Please sign in.');
                setTimeout(() => {
                    onSuccess();
                }, 1500);
            } else {
                showError(response.data.message || 'Failed to create account');
            }
        } catch (error) {
            showError(error.response?.data?.message || 'Failed to create account');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="create-account-wrapper">
            <div className="create-account-left">
                <form onSubmit={handleSubmit} className="login-form create-account-form">
                    <div className="form-group">
                        <label htmlFor="name">Full Name *</label>
                        <input
                            id="name"
                            name="name"
                            type="text"
                            value={formData.name}
                            onChange={handleChange}
                            placeholder="Enter your full name"
                            className="form-input"
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="email">Email address *</label>
                        <input
                            id="email"
                            name="email"
                            type="email"
                            value={formData.email}
                            onChange={handleChange}
                            placeholder="Enter your email"
                            className="form-input"
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="phone">Phone Number *</label>
                        <div className="phone-input-container">
                            <span className="phone-prefix">+</span>
                            <input
                                id="phone"
                                name="phone"
                                type="tel"
                                value={formData.phone}
                                onChange={handleChange}
                                placeholder="Country code and number (e.g., 1234567890)"
                                className="form-input phone-input"
                            />
                        </div>
                    </div>

                    <div className="form-group">
                        <label htmlFor="password">Password *</label>
                        <div className="password-input-container">
                            <input
                                id="password"
                                name="password"
                                type={showPassword ? 'text' : 'password'}
                                value={formData.password}
                                onChange={handleChange}
                                placeholder="Enter your password"
                                className="form-input"
                            />
                            <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                className="password-toggle"
                            >
                                {showPassword ? '👁️' : '👁️‍🗨️'}
                            </button>
                        </div>
                    </div>

                    <div className="form-group">
                        <label htmlFor="confirmPassword">Confirm Password *</label>
                        <input
                            id="confirmPassword"
                            name="confirmPassword"
                            type={showPassword ? 'text' : 'password'}
                            value={formData.confirmPassword}
                            onChange={handleChange}
                            placeholder="Confirm your password"
                            className="form-input"
                        />
                    </div>

                    <div className="form-actions" style={{ display: 'flex', gap: '10px' }}>
                        <button
                            type="button"
                            onClick={onCancel}
                            className="btn-secondary"
                            style={{ flex: 1 }}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={isLoading}
                            className="btn-primary"
                            style={{ flex: 1 }}
                        >
                            {isLoading ? 'Creating...' : 'Create Account'}
                        </button>
                    </div>
                </form>
            </div>

            <div className="create-account-divider">
                <span>OR</span>
            </div>

            <div className="create-account-right">
                <div className="google-signup-section">
                    <h3>Quick Sign Up</h3>
                    <button
                        type="button"
                        onClick={handleGoogleSignup}
                        className="btn-google"
                    >
                        <svg width="18" height="18" viewBox="0 0 18 18">
                            <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" />
                            <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332C2.438 15.983 5.482 18 9 18z" />
                            <path fill="#FBBC05" d="M3.964 10.71c-.18-.54-.282-1.117-.282-1.71s.102-1.17.282-1.71V4.958H.957C.347 6.173 0 7.548 0 9s.348 2.827.957 4.042l3.007-2.332z" />
                            <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0 5.482 0 2.438 2.017.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" />
                        </svg>
                        Sign up with Google
                    </button>
                    <p className="google-signup-note">Sign up quickly with your Google account</p>
                </div>
            </div>
        </div>
    );
};

const ForgotPasswordForm = ({ onCancel, onSuccess, showSuccess, showError }) => {
    const [email, setEmail] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validation
        if (!email) {
            showError('Please enter your email address');
            return;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            showError('Please enter a valid email address');
            return;
        }

        setIsLoading(true);

        try {
            const response = await axios.post(`${API_BASE_URL}/api/auth/resetPassword`, {
                email: email
            });

            if (response.data.success) {
                showSuccess('Password reset link has been sent to your email!');
                setEmail('');
                setTimeout(() => {
                    onSuccess();
                }, 2000);
            } else {
                showError(response.data.message || 'Failed to send reset link');
            }
        } catch (error) {
            showError(error.response?.data?.message || 'Failed to send reset link. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="login-form forgot-password-form">
            <div className="form-group">
                <label htmlFor="reset-email">Email address *</label>
                <input
                    id="reset-email"
                    name="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email address"
                    className="form-input"
                    autoComplete="email"
                />
                <p className="form-helper-text">
                    Enter your email address and we'll send you a new password.
                </p>
            </div>

            <div className="form-actions" style={{ display: 'flex', gap: '10px' }}>
                <button
                    type="button"
                    onClick={onCancel}
                    className="btn-secondary"
                    style={{ flex: 1 }}
                >
                    Cancel
                </button>
                <button
                    type="submit"
                    disabled={isLoading}
                    className="btn-primary"
                    style={{ flex: 1 }}
                >
                    {isLoading ? 'Sending...' : 'Reset Password'}
                </button>
            </div>
        </form>
    );
};

export default Login;
